/**
 * Validates redirect URLs to prevent open redirect attacks.
 * Only allows same-origin paths and whitelisted external domains.
 */

const ALLOWED_DOMAINS = [
  'ofself.ai',
  'app.ofself.ai',
  'api.ofself.ai',
  'www.ofself.ai',
  // .com is the primary domain; .ai is retained and redirects here.
  'ofself.com',
  'nucleus.ofself.com',
  'app.ofself.com',
  'api.ofself.com',
  'www.ofself.com',
];

function isAllowedDomain(hostname) {
  // Allow localhost for development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return true;
  }
  return ALLOWED_DOMAINS.some(
    (d) => hostname === d || hostname.endsWith('.' + d)
  );
}

/**
 * Returns true if the URL is safe to redirect to.
 * Safe means: same-origin relative path, or an allowed external domain.
 */
export function isSafeRedirectUrl(url) {
  if (!url || typeof url !== 'string') return false;

  // A browser reads `\` as `/` and strips tabs and newlines, so `/\evil.com`
  // and `/\t/evil.com` are `//evil.com` — another host — though they start with
  // a single slash. No redirect target needs either.
  // eslint-disable-next-line no-control-regex
  if (/[\\\u0000-\u001f\u007f]/.test(url)) return false;

  // Relative paths are always safe
  if (url.startsWith('/') && !url.startsWith('//')) {
    return true;
  }

  try {
    const parsed = new URL(url, window.location.origin);
    // Must be http or https
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return false;
    }
    return isAllowedDomain(parsed.hostname);
  } catch {
    return false;
  }
}

// WHERE A HANDOFF CODE MAY GO. A `dashboard` or `admin` code is a bearer
// credential: its exchange returns the person's own tokens and asks only for the
// first-party key, which every bundle ships. So "any ofself host" above is far too
// wide for one — script on any subdomain would collect a signed-in person's tokens
// with a link to /handoff — and each audience goes only to the frontend that
// consumes it: `admin` to the console's /callback, `dashboard` to Nucleus (the
// developer portal's "Preview in Nucleus"). Any other audience is not handed off
// from this page at all. PKCE would not help: whoever starts the flow chooses it.
//
// The dev ports (readme_library/apps-and-ports.md) only while this Nucleus is itself
// local, so production never sends a code to whatever listens on someone's machine.
//
// The backend holds the same list and checks it again (backend/config.py,
// HANDOFF_DELIVERY_ORIGINS). Change them together.
const HANDOFF_DESTINATIONS = {
  admin: ['https://admin.ofself.com', 'https://admin.ofself.ai'],
  dashboard: ['https://nucleus.ofself.com', 'https://app.ofself.com',
              'https://nucleus.ofself.ai', 'https://app.ofself.ai'],
};
const HANDOFF_LOCAL_DESTINATIONS = {
  admin: ['http://localhost:3009', 'http://127.0.0.1:3009'],
  dashboard: ['http://localhost:3005', 'http://127.0.0.1:3005'],
};

/**
 * True when a handoff code for `audience` may be sent to `url`. `here` is this
 * page's location (a parameter so a test can say where Nucleus is running).
 */
export function isHandoffDestination(audience, url, here = window.location) {
  if (!Object.prototype.hasOwnProperty.call(HANDOFF_DESTINATIONS, audience)) return false;
  if (!isSafeRedirectUrl(url)) return false;     // the backslash and control-character refusals
  let origin;
  try {
    origin = new URL(url).origin;                // absolute only: a relative path stays here
  } catch {
    return false;
  }
  const local = here?.hostname === 'localhost' || here?.hostname === '127.0.0.1';
  return HANDOFF_DESTINATIONS[audience].includes(origin)
    || (local && HANDOFF_LOCAL_DESTINATIONS[audience].includes(origin));
}
